import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'

const modelOptions = [
  'gpt-4o-mini',
  'gpt-4o',
  'gemini-2.5-flash-lite',
  'gemini-2.5-flash',
]

const MODEL_COSTS: Record<string, { input: number; output: number }> = {
  'gpt-4o': { input: 0.0025, output: 0.01 },
  'gpt-4o-mini': { input: 0.00015, output: 0.0006 },
  'gemini-2.5-flash': { input: 0.0003, output: 0.0025 },
  'gemini-2.5-flash-lite': { input: 0.0001, output: 0.0004 },
}

const TASK_TOKEN_ESTIMATES: Record<string, { input: number; output: number }> = {
  summary_M2: { input: 4000, output: 2000 },
  qa_M3: { input: 4000, output: 1500 },
  terms_M4: { input: 4000, output: 2000 },
  examples_M5: { input: 4000, output: 1500 },
  mcq_P1: { input: 5000, output: 4000 },
  content_P2_P4: { input: 5000, output: 3000 },
  marketing_P6: { input: 3000, output: 2500 },
  podcast_P7: { input: 4000, output: 3000 },
  esp_P9: { input: 5000, output: 5000 },
  content_map_M6: { input: 8000, output: 12000 },
}

function estimateCostForTask(task: string, model: string): number {
  const rates = MODEL_COSTS[model] || { input: 0.001, output: 0.005 }
  const tokens = TASK_TOKEN_ESTIMATES[task] || { input: 3000, output: 2000 }
  return (tokens.input / 1000) * rates.input + (tokens.output / 1000) * rates.output
}

interface ModelConfig {
  task: string
  task_ar: string
  model: string
}

const defaultModels: ModelConfig[] = [
  { task: 'summary_M2', task_ar: 'ملخصات M2', model: 'gpt-4o-mini' },
  { task: 'qa_M3', task_ar: 'أسئلة المحاضر M3', model: 'gpt-4o-mini' },
  { task: 'terms_M4', task_ar: 'مصطلحات M4', model: 'gpt-4o-mini' },
  { task: 'examples_M5', task_ar: 'أمثلة M5', model: 'gpt-4o-mini' },
  { task: 'mcq_P1', task_ar: 'أسئلة MCQ P1', model: 'gpt-4o-mini' },
  { task: 'content_P2_P4', task_ar: 'محتوى P4 + خريطة ذهنية P8', model: 'gpt-4o-mini' },
  { task: 'marketing_P6', task_ar: 'تسويقي P6a/P6b/P6c', model: 'gpt-4o-mini' },
  { task: 'podcast_P7', task_ar: 'بودكاست P7', model: 'gpt-4o-mini' },
  { task: 'esp_P9', task_ar: 'ESP محتوى لغوي P9', model: 'gpt-4o-mini' },
  { task: 'content_map_M6', task_ar: 'خريطة المحتوى M6', model: 'gpt-4o-mini' },
]

export default function Settings() {
  const { user } = useAuth()
  const isSuperadmin = user?.role === 'superadmin'

  const [models, setModels] = useState(defaultModels)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [actualCost, setActualCost] = useState<number | null>(null)

  // Admin management state
  const [admins, setAdmins] = useState<any[]>([])
  const [showAddAdmin, setShowAddAdmin] = useState(false)
  const [newAdmin, setNewAdmin] = useState({ username: '', password: '', display_name: '', role: 'admin' })
  const [adminError, setAdminError] = useState('')
  const [adminSaving, setAdminSaving] = useState(false)

  useEffect(() => {
    async function load() {
      try {
        const settings = await api.getSettings()
        if (settings.model_config) {
          const merged = defaultModels.map((d) => {
            const saved = settings.model_config.find((s: any) => s.task === d.task)
            return saved ? { ...d, model: saved.model } : d
          })
          setModels(merged)
        }
      } catch {}
      try {
        const stats = await api.getStats()
        setActualCost(stats.total_cost || 0)
      } catch {}
      if (isSuperadmin) {
        try {
          const users = await api.listAdmins()
          setAdmins(users)
        } catch {}
      }
    }
    load()
  }, [isSuperadmin])

  async function handleSave() {
    setSaving(true)
    try {
      await api.updateSettings({ model_config: models })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  async function handleAddAdmin() {
    if (!newAdmin.username || !newAdmin.password) { setAdminError('اسم المستخدم وكلمة المرور مطلوبين'); return }
    setAdminSaving(true)
    setAdminError('')
    try {
      await api.createAdmin(newAdmin)
      const users = await api.listAdmins()
      setAdmins(users)
      setNewAdmin({ username: '', password: '', display_name: '', role: 'admin' })
      setShowAddAdmin(false)
    } catch (err: any) {
      let msg = 'فشل الإنشاء'
      try { msg = JSON.parse(err.message).error || msg } catch {}
      setAdminError(msg)
    } finally {
      setAdminSaving(false)
    }
  }

  async function handleToggleAdmin(id: string, currentActive: boolean) {
    try {
      await api.updateAdmin(id, { is_active: !currentActive })
      const users = await api.listAdmins()
      setAdmins(users)
    } catch {}
  }

  async function handleDeleteAdmin(id: string, username: string) {
    if (!confirm(`حذف ${username}?`)) return
    try {
      await api.deleteAdmin(id)
      const users = await api.listAdmins()
      setAdmins(users)
    } catch {}
  }

  const taskCosts = models.map((m) => ({
    ...m,
    cost: estimateCostForTask(m.task, m.model),
  }))
  const aiTotal = taskCosts.reduce((sum, t) => sum + t.cost, 0)

  return (
    <div>
      <h2 className="text-xl font-bold text-vertex-gold mb-6">الإعدادات</h2>

      {actualCost !== null && actualCost > 0 && (
        <div className="bg-vertex-navy rounded-lg border border-vertex-surface p-4 mb-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold">الإنفاق الفعلي</h3>
            <span className="text-vertex-gold font-bold text-lg" dir="ltr">${actualCost.toFixed(4)}</span>
          </div>
        </div>
      )}

      {/* ==================== Admin Management ==================== */}
      {isSuperadmin && (
        <div className="bg-vertex-navy rounded-lg border border-vertex-surface p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold">إدارة المستخدمين</h3>
            <button
              onClick={() => setShowAddAdmin(!showAddAdmin)}
              className="text-xs bg-vertex-gold text-vertex-deep px-3 py-1 rounded font-bold hover:bg-vertex-gold-light transition-colors"
            >
              {showAddAdmin ? 'إلغاء' : '+ أدمن جديد'}
            </button>
          </div>

          {/* Add admin form */}
          {showAddAdmin && (
            <div className="bg-vertex-deep rounded p-3 mb-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  placeholder="اسم المستخدم"
                  value={newAdmin.username}
                  onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                  className="bg-vertex-surface border border-vertex-surface rounded px-2 py-1.5 text-xs text-white focus:border-vertex-gold outline-none"
                  dir="ltr"
                />
                <input
                  type="password"
                  placeholder="كلمة المرور (6+ حروف)"
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                  className="bg-vertex-surface border border-vertex-surface rounded px-2 py-1.5 text-xs text-white focus:border-vertex-gold outline-none"
                  dir="ltr"
                />
                <input
                  type="text"
                  placeholder="الاسم الظاهر"
                  value={newAdmin.display_name}
                  onChange={(e) => setNewAdmin({ ...newAdmin, display_name: e.target.value })}
                  className="bg-vertex-surface border border-vertex-surface rounded px-2 py-1.5 text-xs text-white focus:border-vertex-gold outline-none"
                />
                <select
                  value={newAdmin.role}
                  onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}
                  className="bg-vertex-surface border border-vertex-surface rounded px-2 py-1.5 text-xs text-white focus:border-vertex-gold outline-none"
                >
                  <option value="admin">Admin</option>
                  <option value="superadmin">Superadmin</option>
                </select>
              </div>
              {adminError && <p className="text-xs text-red-400">{adminError}</p>}
              <button
                onClick={handleAddAdmin}
                disabled={adminSaving}
                className="bg-vertex-gold text-vertex-deep px-4 py-1.5 rounded text-xs font-bold hover:bg-vertex-gold-light disabled:opacity-50 transition-colors"
              >
                {adminSaving ? '...' : 'إضافة'}
              </button>
            </div>
          )}

          {/* Admin list */}
          <div className="space-y-1">
            {admins.map((admin) => (
              <div key={admin.id} className="flex items-center justify-between bg-vertex-deep rounded px-3 py-2">
                <div className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full ${admin.is_active ? 'bg-green-400' : 'bg-gray-600'}`} />
                  <div>
                    <span className="text-xs text-white">{admin.display_name || admin.username}</span>
                    <span className="text-[10px] text-gray-500 mr-2" dir="ltr">@{admin.username}</span>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${admin.role === 'superadmin' ? 'bg-vertex-gold/20 text-vertex-gold' : 'bg-vertex-surface text-gray-400'}`}>
                    {admin.role}
                  </span>
                </div>
                {admin.id !== user?.id && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleAdmin(admin.id, admin.is_active)}
                      className={`text-[10px] px-2 py-0.5 rounded ${admin.is_active ? 'text-yellow-400 hover:bg-yellow-900/20' : 'text-green-400 hover:bg-green-900/20'}`}
                    >
                      {admin.is_active ? 'تعطيل' : 'تفعيل'}
                    </button>
                    <button
                      onClick={() => handleDeleteAdmin(admin.id, admin.username)}
                      className="text-[10px] text-red-400 hover:bg-red-900/20 px-2 py-0.5 rounded"
                    >
                      حذف
                    </button>
                  </div>
                )}
                {admin.id === user?.id && (
                  <span className="text-[10px] text-gray-600">أنت</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* API Keys Info */}
      <div className="bg-vertex-navy rounded-lg border border-vertex-surface p-4 mb-6">
        <h3 className="text-sm font-bold mb-2">مفاتيح الـ API</h3>
        <p className="text-xs text-gray-400">
          المفاتيح محفوظة كـ Secrets في Cloudflare Dashboard.
        </p>
        <p className="text-xs text-gray-500 mt-1" dir="ltr">
          Cloudflare Dashboard → Pages → vertex-content-factory → Settings → Environment variables
        </p>
        <div className="flex gap-3 mt-3">
          {['OpenAI', 'Gemini'].map((p) => (
            <span key={p} className="bg-vertex-surface px-3 py-1 rounded text-xs text-green-400">
              {p}
            </span>
          ))}
        </div>
      </div>

      {/* V2 Architecture Note */}
      <div className="bg-vertex-navy rounded-lg border border-green-900/30 p-4 mb-6">
        <h3 className="text-sm font-bold text-green-400 mb-2">V2 — بدون ترجمة + معالجة ميكانيكية</h3>
        <div className="text-xs text-gray-400 space-y-1">
          <p>M1 (تقسيم): ميكانيكي — بدون AI</p>
          <p>P3 (مصطلحات): ميكانيكي من M4 — بدون AI</p>
          <p>P5 (بطاقات): ميكانيكي من M4 — بدون AI</p>
          <p>الترانسكريبت: يُسحب AR + EN مباشرة من YouTube — بدون ترجمة</p>
        </div>
      </div>

      {/* Model Selection — AI tasks only */}
      <div className="bg-vertex-navy rounded-lg border border-vertex-surface p-4 mb-6">
        <h3 className="text-sm font-bold mb-4">توزيع النماذج على المهام (AI فقط)</h3>
        <div className="space-y-2">
          {taskCosts.map((config, i) => (
            <div key={config.task} className="flex items-center gap-3">
              <span className="w-40 text-xs text-gray-400">{config.task_ar}:</span>
              <select
                value={config.model}
                onChange={(e) => {
                  const updated = [...models]
                  updated[i] = { ...models[i], model: e.target.value }
                  setModels(updated)
                }}
                className="flex-1 bg-vertex-deep border border-vertex-surface rounded px-3 py-2 text-xs text-white focus:border-vertex-gold outline-none"
              >
                {modelOptions.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <span className="text-xs text-gray-500 w-20 text-left font-mono" dir="ltr">
                ${config.cost.toFixed(4)}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-vertex-surface space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-gray-400">تكلفة AI لمحاضرة واحدة:</span>
            <span className="text-vertex-gold font-bold" dir="ltr">~${aiTotal.toFixed(4)}</span>
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>التكلفة لـ 47 محاضرة:</span>
            <span dir="ltr">~${(aiTotal * 47).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-xs text-green-600">
            <span>التوفير عن V1 (بالترجمة):</span>
            <span dir="ltr">~40%+ وفّرنا</span>
          </div>
        </div>

        <div className="mt-3 p-3 bg-vertex-deep rounded text-xs text-gray-500">
          <p className="mb-1 font-bold text-gray-400">V2 — ما يحتاج AI:</p>
          <p>• M2 ملخصات, M3 أسئلة, M4 مصطلحات, M5 أمثلة</p>
          <p>• P1 MCQ, P4 مراجعة, P6a/b تسويق, P7 بودكاست</p>
          <p className="mt-1 font-bold text-green-600">ما يحتاج AI:</p>
          <p>• الترانسكريبت (من YouTube مباشرة)</p>
          <p>• M1 تقسيم, P3 مصطلحات, P5 بطاقات (ميكانيكي)</p>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="bg-vertex-gold text-vertex-deep px-6 py-2 rounded text-sm font-bold hover:bg-vertex-gold-light disabled:opacity-50 transition-colors"
      >
        {saving ? 'جاري الحفظ...' : saved ? 'تم الحفظ' : 'حفظ الإعدادات'}
      </button>
    </div>
  )
}
